// apps/web/src/lib/vendas/filtros.ts — Fase 12, Plano 04 (PV-05, PV-06).
// Filtros da lista de Pedidos: serialização em query string e apresentação da situação
// derivada. Funções puras: quem filtra e ordena é o servidor (`GET /api/v1/vendas/pedidos`).
import type { ClienteLookupPv, NivelPrioridadeKanban, PrazoFiltroPv, PrioridadePv, SituacaoPv } from '@pieltec/contracts';
import { hojeIso } from './formatacao';

export interface FiltrosPv {
  busca: string;
  cliente: ClienteLookupPv | null;
  situacoes: readonly SituacaoPv[];
  prazo: PrazoFiltroPv;
  prazoDe: string;
  prazoAte: string;
  incluirEncerrados: boolean;
}

export const SITUACOES_LISTA_PV: readonly SituacaoPv[] = ['ABERTO', 'EM_RECEBIMENTO', 'EM_PRODUCAO', 'PARCIALMENTE_FATURADO', 'CONCLUIDO', 'CANCELADO'];

export const OPCOES_PRAZO_PV: ReadonlyArray<{ readonly valor: PrazoFiltroPv; readonly rotulo: string }> = [
  { valor: 'TODOS', rotulo: 'Todos os prazos' },
  { valor: 'ATRASADOS', rotulo: 'Atrasados' },
  { valor: 'PROXIMOS_7', rotulo: 'Próximos 7 dias' },
  { valor: 'PERIODO', rotulo: 'Período…' },
];

export function filtrosPvVazios(): FiltrosPv {
  return { busca: '', cliente: null, situacoes: [], prazo: 'TODOS', prazoDe: '', prazoAte: '', incluirEncerrados: false };
}

/** Há algum filtro além do padrão (busca vazia, sem cliente/situação/prazo e sem encerrados)? */
export function filtrosPvAtivos(filtros: FiltrosPv): boolean {
  return (
    filtros.busca.trim() !== '' ||
    filtros.cliente !== null ||
    filtros.situacoes.length > 0 ||
    filtros.prazo !== 'TODOS' ||
    filtros.incluirEncerrados
  );
}

/** Período só vale com as duas datas; enquanto incompleto, o prazo não é enviado (a API exigiria as duas). */
function prazoEfetivo(filtros: FiltrosPv): PrazoFiltroPv {
  if (filtros.prazo === 'PERIODO' && (filtros.prazoDe === '' || filtros.prazoAte === '')) return 'TODOS';
  return filtros.prazo;
}

/** Query string (sem `?`) com só os filtros ativos, na ordem fixa dos contratos. */
export function serializarFiltrosPv(filtros: FiltrosPv, paginacao: { pagina: number; tamanhoPagina: number }): string {
  const params = new URLSearchParams();
  params.set('pagina', String(paginacao.pagina));
  params.set('tamanhoPagina', String(paginacao.tamanhoPagina));
  const busca = filtros.busca.trim();
  if (busca !== '') params.set('busca', busca);
  if (filtros.cliente) params.set('clienteId', filtros.cliente.id);
  if (filtros.situacoes.length > 0) {
    params.set('situacoes', SITUACOES_LISTA_PV.filter((situacao) => filtros.situacoes.includes(situacao)).join(','));
  }
  const prazo = prazoEfetivo(filtros);
  if (prazo !== 'TODOS') {
    params.set('prazo', prazo);
    if (prazo === 'PERIODO') {
      params.set('prazoDe', filtros.prazoDe);
      params.set('prazoAte', filtros.prazoAte);
    }
  }
  if (filtros.incluirEncerrados) params.set('incluirEncerrados', 'true');
  return params.toString();
}

// Texto + ícone + cor da situação vivem em ./apresentacao (paleta fechada do UI-SPEC, plano 12-21).
export { rotuloSituacaoPv } from './apresentacao';

/** '{N} pendência(s)'; zero vira traço. */
export function textoPendencias(total: number): string {
  if (total <= 0) return '—';
  return total === 1 ? '1 pendência' : `${total} pendências`;
}

/** Mesmos patamares do Kanban: 20 urgente, 10 alta, demais normal. */
export function nivelDaPrioridadePv(prioridade: PrioridadePv): NivelPrioridadeKanban {
  if (prioridade >= 20) return 'urgente';
  if (prioridade >= 10) return 'alta';
  return 'normal';
}

const DIAS_PRAZO_PERTO = 2;

function diasEntre(deIso: string, ateIso: string): number {
  const [a1, m1, d1] = deIso.split('-').map(Number);
  const [a2, m2, d2] = ateIso.split('-').map(Number);
  return Math.round((Date.UTC(a2 ?? 0, (m2 ?? 1) - 1, d2 ?? 1) - Date.UTC(a1 ?? 0, (m1 ?? 1) - 1, d1 ?? 1)) / 86_400_000);
}

export { hojeIso };

/** Urgência do prazo do PV para a faixa lateral: encerrado ou sem prazo é sempre 'ok'. */
export function situacaoPrazoPv(prazoSolicitado: string | null, situacao: SituacaoPv, hoje: string = hojeIso()): 'ok' | 'perto' | 'estourado' {
  if (!prazoSolicitado || situacao === 'CONCLUIDO' || situacao === 'CANCELADO') return 'ok';
  const dias = diasEntre(hoje, prazoSolicitado.slice(0, 10));
  if (dias < 0) return 'estourado';
  if (dias <= DIAS_PRAZO_PERTO) return 'perto';
  return 'ok';
}
